import { COMPANY_NUMBER, ENTITY, ENTITY_ADDRESS, LEGAL_UPDATED, supportEmail } from '../legal'

/** Privacy notice (UK GDPR art. 13). Public: reachable signed in or out. */
export default function Privacy() {
  const email = supportEmail()
  return (
    <>
      <h1>Privacy notice</h1>
      <p className="muted">Last updated {LEGAL_UPDATED}</p>

      <p>
        Flightdeck is run by {ENTITY} ("we", "us"), a company registered in England and Wales
        (number {COMPANY_NUMBER}) whose registered office is {ENTITY_ADDRESS}. We are the
        controller of the personal data described here. Questions, requests and complaints:{' '}
        <a href={`mailto:${email}`}>{email}</a>.
      </p>

      <h2>What we collect</h2>
      <ul>
        <li>
          <strong>Account:</strong> your email address and password. The password is held by
          Amazon Cognito as a salted hash; we cannot read it. If you turn on an authenticator
          app for two-step sign-in, its secret is held there too.
        </li>
        <li>
          <strong>Your content:</strong> the decks and cards you write, the images you upload
          (and the resized copies we make of them), your quiz sessions and your scores over
          time.
        </li>
        <li>
          <strong>Plan and billing:</strong> your plan, usage counters, and your Stripe customer
          and subscription identifiers. Card details go to Stripe directly and never reach us.
        </li>
        <li>
          <strong>Connected apps:</strong> which AI assistants or other apps you have allowed to
          use your account, and when each was last used.
        </li>
        <li>
          <strong>Technical records:</strong> your IP address, a request identifier, your account
          identifier, and the time, method, path and result of each request. These are kept in
          server logs for security and for investigating abuse.
        </li>
      </ul>
      <p>
        We do not run advertising or analytics trackers, and we do not sell your data.
      </p>

      <h2>Why we use it, and our legal basis</h2>
      <ul>
        <li>
          To provide the service you signed up for: your account, decks, images, quizzes and
          subscription. <em>Basis: contract.</em>
        </li>
        <li>
          To keep the service secure, stop abuse, and investigate reports, which is why we keep
          technical records and check uploaded images automatically.{' '}
          <em>Basis: legitimate interests.</em>
        </li>
        <li>
          To send service emails: verification codes, password resets, and a digest telling you
          when decks have been inactive and will be archived. <em>Basis: contract.</em>
        </li>
        <li>
          To keep tax and accounting records for payments. <em>Basis: legal obligation.</em>
        </li>
      </ul>

      <h2>Who handles it for us</h2>
      <p>
        We use these processors, each under their own data-protection terms:
      </p>
      <ul>
        <li>
          <strong>Amazon Web Services:</strong> hosting, storage, sign-in (Cognito), email
          (SES), content delivery and web firewall. Your data is stored in the London region.
          Pages and images are delivered through a global network of edge locations, and some
          firewall logs are kept in the US East (N. Virginia) region.
        </li>
        <li>
          <strong>Amazon Rekognition</strong> (part of AWS): every uploaded image is analysed
          automatically for content that breaks our acceptable-use rules. It is not used to
          identify people.
        </li>
        <li>
          <strong>Stripe:</strong> payments, subscriptions and tax. Stripe is a controller of
          the payment data it collects itself, under its own privacy policy, and may process
          data outside the UK under appropriate safeguards.
        </li>
      </ul>
      <p>
        We do not share your content with anyone else. Your decks and images are private to
        your account. We will disclose data if the law requires it, or to report illegal
        material to the authorities.
      </p>

      <h2>How long we keep it</h2>
      <ul>
        <li>
          <strong>Your account and content:</strong> until you delete the account, or until a
          deck is removed by the inactivity process below.
        </li>
        <li>
          <strong>Inactive decks:</strong> a deck you have not opened for 90 days (180 on a paid
          plan) is flagged and you are emailed. If it stays untouched it is archived 30 days
          later (60 on a paid plan), where you can restore it, and permanently deleted a year
          after that.
        </li>
        <li>
          <strong>Server logs:</strong> 90 days.
        </li>
        <li>
          <strong>Images we have rejected or removed for breaking the rules:</strong> up to a
          year, so we can review a decision or report illegal material.
        </li>
        <li>
          <strong>Payment records:</strong> as long as tax law requires, normally six years.
        </li>
        <li>
          <strong>Backups:</strong> deleted data can remain in database backups for up to 35
          days before it is overwritten.
        </li>
      </ul>

      <h2>Cookies and local storage</h2>
      <p>
        We set one cookie, which keeps you signed in. It is strictly necessary for the service
        to work, so it needs no consent banner. Your light or dark theme choice is stored in
        your browser's local storage. Nothing else is stored.
      </p>

      <h2>Your rights</h2>
      <p>
        You can ask for a copy of your data, to correct it, to delete it, to restrict or object
        to how we use it, or to receive it in a portable format. You can delete your account and
        everything attached to it yourself, at any time, from the Account page: this cancels
        your subscription and removes your decks, images and profile. For anything else, email{' '}
        <a href={`mailto:${email}`}>{email}</a> and we will reply within one month.
      </p>
      <p>
        If you are unhappy with how we handle your data you can complain to the Information
        Commissioner's Office at <a href="https://ico.org.uk">ico.org.uk</a>, though we would
        like the chance to put it right first.
      </p>

      <h2>Children</h2>
      <p>
        Flightdeck is not intended for children under 13, and we do not knowingly collect their
        data. If you are under 18, a parent or guardian must agree before you buy a paid plan.
      </p>

      <h2>Changes</h2>
      <p>
        If we change this notice in a way that matters, we will update the date above and, for
        a significant change, email you.
      </p>
    </>
  )
}
